import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import { 
  Mountain,
  FileText,
  Building2,
  Users,
  AlertTriangle,
  ClipboardCheck,
  MessageSquare,
  Calendar,
  CreditCard,
  Map,
  Bell,
  Bot,
  LogOut,
  BarChart3,
  Home,
  Megaphone,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  Zap,
  HelpCircle
} from "lucide-react";

const PropertyManagerDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [token]);

  const fetchStats = async () => {
    try {
      const [properties, owners, violations, drc, payments] = await Promise.all([
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/owners`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/violations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/drc`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/payments/dashboard/summary`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
      ]);
      
      setStats({
        total_properties: properties.data?.length || 0,
        total_owners: owners.data?.length || 0,
        open_violations: violations.data?.filter(v => v.status !== "resolved").length || 0,
        pending_drc: drc.data?.filter(d => ["submitted", "under_review"].includes(d.status)).length || 0,
        total_collected: payments.data?.total_collected || 0,
        total_pending: payments.data?.total_pending || 0
      });
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const navLinks = [
    { icon: <BarChart3 className="w-4 h-4" />, label: "Dashboard", href: "/property-manager" },
    { icon: <FileText className="w-4 h-4" />, label: "Documents", href: "/board/documents" },
    { icon: <Building2 className="w-4 h-4" />, label: "Properties", href: "/board/properties" },
    { icon: <Users className="w-4 h-4" />, label: "Owners", href: "/board/owners" },
    { icon: <AlertTriangle className="w-4 h-4" />, label: "Notices", href: "/board/violations" },
    { icon: <ClipboardCheck className="w-4 h-4" />, label: "DRC", href: "/board/drc" },
    { icon: <MessageSquare className="w-4 h-4" />, label: "Correspondence", href: "/board/correspondence" },
    { icon: <MessageSquare className="w-4 h-4" />, label: "Forum", href: "/board/forum" },
    { icon: <Calendar className="w-4 h-4" />, label: "Meetings", href: "/board/meetings" },
    { icon: <CreditCard className="w-4 h-4" />, label: "Payments", href: "/board/payments" },
    { icon: <Map className="w-4 h-4" />, label: "GIS Map", href: "/board/gis" },
    { icon: <Bell className="w-4 h-4" />, label: "Announcements", href: "/board/announcements" },
    { icon: <Bot className="w-4 h-4" />, label: "AI Assistant", href: "/board/ai" },
    { icon: <DollarSign className="w-4 h-4" />, label: "Accounting", href: "/accountant" },
    { icon: <Home className="w-4 h-4" />, label: "Listing Toolkit", href: "/board/listing" },
    { icon: <Zap className="w-4 h-4" />, label: "Automation", href: "/board/automation" },
    { icon: <MessageSquare className="w-4 h-4" />, label: "Communications", href: "/board/communications" },
    { icon: <ClipboardCheck className="w-4 h-4" />, label: "Workflow Hub", href: "/board/workflow" },
    { icon: <HelpCircle className="w-4 h-4" />, label: "Knowledge Base", href: "/admin/knowledge-base" }
  ];

  const statCards = [
    { 
      label: "Properties", 
      value: stats?.total_properties || 0, 
      icon: <Building2 className="w-6 h-6 text-primary" />,
      bg: "bg-primary/10",
      href: "/board/properties"
    },
    { 
      label: "Owners", 
      value: stats?.total_owners || 0, 
      icon: <Users className="w-6 h-6 text-info" />,
      bg: "bg-info/10",
      href: "/board/owners"
    },
    { 
      label: "Open Notices", 
      value: stats?.open_violations || 0, 
      icon: <AlertTriangle className="w-6 h-6 text-destructive" />,
      bg: "bg-destructive/10",
      href: "/board/violations"
    },
    { 
      label: "Pending DRC", 
      value: stats?.pending_drc || 0, 
      icon: <ClipboardCheck className="w-6 h-6 text-warning" />,
      bg: "bg-warning/10",
      href: "/board/drc"
    }
  ];

  const quickActions = [
    { label: "New Notice", icon: <AlertTriangle className="w-5 h-5" />, href: "/board/violations", color: "text-destructive" },
    { label: "Review DRC", icon: <ClipboardCheck className="w-5 h-5" />, href: "/board/drc", color: "text-primary" },
    { label: "Send Announcement", icon: <Megaphone className="w-5 h-5" />, href: "/board/announcements", color: "text-secondary" },
    { label: "View Payments", icon: <CreditCard className="w-5 h-5" />, href: "/board/payments", color: "text-success" }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-charcoal text-sand sticky top-0 z-50 backdrop-blur-md bg-charcoal/95">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mountain className="w-7 h-7 text-copper" />
            <div>
              <span className="font-serif text-lg font-semibold">Horse Mountain Ranch</span>
              <Badge className="ml-2 bg-info/20 text-info">PROPERTY MANAGER</Badge>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-sand/70 hidden sm:inline">{user?.name}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-sand/70 hover:text-sand hover:bg-white/10 transition-colors duration-200"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="border-b bg-card/95 backdrop-blur-sm sticky top-[73px] z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-1 overflow-x-auto py-2 scrollbar-hide">
            {navLinks.map((link, index) => {
              const isActive = location.pathname === link.href;
              return (
                <Link key={index} to={link.href}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`flex items-center gap-2 transition-all duration-200 ${
                      isActive 
                        ? "bg-primary/10 text-primary font-medium" 
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                  >
                    {link.icon}
                    <span className="hidden sm:inline">{link.label}</span>
                  </Button>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-2">
            Property Manager Dashboard
          </h1>
          <p className="text-muted-foreground text-lg">
            Full operational access to manage Horse Mountain Ranch HOA.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          {statCards.map((stat, index) => (
            <Link key={index} to={stat.href}>
              <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group cursor-pointer" style={{ animationDelay: `${index * 0.1}s` }}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{stat.label}</p>
                      <p className="text-3xl font-bold text-foreground mt-1">
                        {loading ? <span className="skeleton-luxury inline-block w-12 h-8 rounded" /> : stat.value}
                      </p>
                    </div>
                    <div className={`w-12 h-12 ${stat.bg} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      {stat.icon}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Financial Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="border-border/50 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
            <CardHeader>
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-success" />
                Collections
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Total Collected</span>
                  <span className="text-2xl font-bold text-success">
                    ${loading ? "..." : (stats?.total_collected || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Pending</span>
                  <span className="text-xl font-semibold text-warning">
                    ${loading ? "..." : (stats?.total_pending || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/50 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
            <CardHeader>
              <CardTitle className="font-serif text-xl">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {quickActions.map((action, index) => (
                  <Link key={index} to={action.href}>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start gap-2 hover:border-primary/50 transition-colors duration-200"
                    >
                      <span className={action.color}>{action.icon}</span>
                      {action.label}
                    </Button>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Module Grid */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="font-serif text-xl">All Modules</CardTitle>
            <CardDescription>Access any module from here</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {[
                { label: "Documents", icon: <FileText className="w-5 h-5" />, href: "/board/documents", color: "text-primary" },
                { label: "Properties", icon: <Building2 className="w-5 h-5" />, href: "/board/properties", color: "text-primary" },
                { label: "Owners", icon: <Users className="w-5 h-5" />, href: "/board/owners", color: "text-info" },
                { label: "Notices", icon: <AlertTriangle className="w-5 h-5" />, href: "/board/violations", color: "text-destructive" },
                { label: "DRC", icon: <ClipboardCheck className="w-5 h-5" />, href: "/board/drc", color: "text-warning" },
                { label: "Correspondence", icon: <MessageSquare className="w-5 h-5" />, href: "/board/correspondence", color: "text-secondary" },
                { label: "Forum", icon: <MessageSquare className="w-5 h-5" />, href: "/board/forum", color: "text-purple-500" },
                { label: "Meetings", icon: <Calendar className="w-5 h-5" />, href: "/board/meetings", color: "text-info" },
                { label: "Payments", icon: <CreditCard className="w-5 h-5" />, href: "/board/payments", color: "text-success" },
                { label: "Accounting", icon: <DollarSign className="w-5 h-5" />, href: "/accountant", color: "text-success" },
                { label: "GIS Map", icon: <Map className="w-5 h-5" />, href: "/board/gis", color: "text-teal-500" },
                { label: "Announcements", icon: <Megaphone className="w-5 h-5" />, href: "/board/announcements", color: "text-indigo-500" }
              ].map((module, index) => (
                <Link key={index} to={module.href}>
                  <Card className="hover:-translate-y-1 hover:shadow-floating transition-all duration-300 cursor-pointer border-border/50 hover:border-border">
                    <CardContent className="pt-4 pb-4 flex flex-col items-center text-center">
                      <div className={`w-10 h-10 rounded-lg bg-current/10 flex items-center justify-center mb-2 ${module.color}`}>
                        {module.icon}
                      </div>
                      <span className="text-sm font-medium">{module.label}</span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PropertyManagerDashboard;
